import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { DataTableShell } from "./common/DataTableShell";

/**
 * Envoltorio genérico de tabla para las pantallas de lista.
 *
 * Desktop (`>= lg`): DataTableShell + @tanstack/react-table.
 * Móvil (`< lg`): una tarjeta por registro, generada a partir de las mismas
 * columnas (etiqueta = header, valor = cell) — no hay diseño móvil propio
 * para estas pantallas (README, "Sin diseño todavía"), así que esto es el
 * mínimo razonable que cumple "nada de scroll horizontal en móvil".
 *
 * `minWidth` es el ancho mínimo de la tabla de escritorio (ver
 * DataTableShell): Órdenes 1320 · Usuarios 1560, etc. — lo define cada
 * pantalla, no este componente.
 */
export default function DataTable({
  data,
  columns,
  total = 0,
  pagination,
  setPagination,
  progressPending,
  minWidth,
  emptyMessage,
  filters,
}) {
  // Un puñado de modales sin diseño todavía (ver README) siguen pasando el
  // `pagination` booleano de react-data-table-component en vez del objeto
  // {pageIndex, pageSize}. Sin este resguardo se ven "NaN" en el pie de
  // tabla; con él, al menos muestran algo coherente aunque no paginen.
  if (!pagination || typeof pagination.pageIndex !== "number") {
    pagination = { pageIndex: 0, pageSize: 10 };
  }

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.max(1, Math.ceil(total / pagination.pageSize)),
    state: { pagination },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
  });

  const rows = table.getRowModel().rows;
  const headerCells = table.getHeaderGroups()[0]?.headers ?? [];
  const isEmpty = !progressPending && rows.length === 0;

  const from = total > 0 ? pagination.pageIndex * pagination.pageSize + 1 : 0;
  const to = Math.min((pagination.pageIndex + 1) * pagination.pageSize, total);
  const canPrev = pagination.pageIndex > 0;
  const canNext = (pagination.pageIndex + 1) * pagination.pageSize < total;

  const footer = (
    <>
      <span>{total > 0 ? `Mostrando ${from}-${to} de ${total}` : "Sin registros"}</span>
      <div className="d-flex align-items-center gap-2">
        <select
          className="ui-page-size"
          value={pagination.pageSize}
          onChange={(e) =>
            setPagination({ ...pagination, pageSize: Number(e.target.value), pageIndex: 0 })
          }
        >
          {[10, 20, 50].map((size) => (
            <option key={size} value={size}>{size} / pág.</option>
          ))}
        </select>
        <button
          type="button"
          className="ui-page-btn"
          disabled={!canPrev}
          onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <span className="ui-page-btn ui-page-btn--active">{pagination.pageIndex + 1}</span>
        <button
          type="button"
          className="ui-page-btn"
          disabled={!canNext}
          onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>
    </>
  );

  const skeletonRowCount = Math.min(pagination.pageSize, 6) || 3;

  return (
    <>
      <div className="d-none d-lg-block">
        <DataTableShell
          filters={filters}
          minWidth={minWidth}
          empty={isEmpty}
          emptyMessage={emptyMessage}
          footer={footer}
          headers={headerCells.map((header) => (
            <th key={header.id}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </th>
          ))}
        >
          {progressPending
            ? Array.from({ length: skeletonRowCount }).map((_, i) => (
                <tr key={`skeleton-${i}`}>
                  {columns.map((_, ci) => (
                    <td key={ci}><span className="ui-skeleton" /></td>
                  ))}
                </tr>
              ))
            : rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
        </DataTableShell>
      </div>

      <div className="d-lg-none d-flex flex-column gap-2">
        {filters && (
          <div className="ui-table-card" style={{ padding: 0 }}>
            <div className="ui-table-card__filters" style={{ borderBottom: 0 }}>{filters}</div>
          </div>
        )}
        {progressPending ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={`m-skeleton-${i}`} className="ui-mobile-card">
              <span className="ui-skeleton" style={{ height: 60 }} />
            </div>
          ))
        ) : isEmpty ? (
          <div className="ui-table__empty">
            <i className="ri-inbox-line" />
            <span>{emptyMessage || "No hay registros para mostrar."}</span>
          </div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="ui-mobile-card">
              {row.getVisibleCells().map((cell, i) => {
                const header = headerCells[i];
                return (
                  <div key={cell.id} className="ui-mobile-card__row">
                    <span className="ui-mobile-card__label">
                      {header ? flexRender(header.column.columnDef.header, header.getContext()) : ""}
                    </span>
                    <span className="ui-mobile-card__value">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </span>
                  </div>
                );
              })}
            </div>
          ))
        )}
        {!isEmpty && (
          <div className="ui-table-card" style={{ padding: 0 }}>
            <div className="ui-table-card__footer">{footer}</div>
          </div>
        )}
      </div>
    </>
  );
}
